import { useEffect, useRef } from "preact/hooks";
import { CANCEL_CODE, bindingFromKeyboardEvent, bindingFromMouseButton, bindingFromWheel } from "../../../settings";
import type { Binding } from "../../../settings";
import { pushEscapeHandler } from "../../../state/route";

export const CAPTURE_PROMPT = "Press a key, mouse button or scroll wheel...";

export interface KeybindCaptureProps {
  onCapture: (binding: Binding) => void;
  onCancel: () => void;
}

/** Events a bound mouse button still produces after its mousedown was captured. */
const PRESS_AFTERMATH = ["click", "auxclick", "contextmenu"] as const;

function stop(e: Event): void {
  e.preventDefault();
  e.stopImmediatePropagation();
}

/**
 * A mouse button bound through its mousedown still delivers mouseup, click and (for the right
 * button) contextmenu once the capture listeners are gone. Keep those away from the page until
 * the button is released, so binding Mouse0 does not press whatever is under the pointer.
 */
function swallowRestOfPress(): void {
  for (const type of PRESS_AFTERMATH) window.addEventListener(type, stop, true);
  window.addEventListener(
    "mouseup",
    (e) => {
      e.preventDefault(); // also stops the browser's back/forward navigation of Mouse3/Mouse4
      window.setTimeout(() => {
        for (const type of PRESS_AFTERMATH) window.removeEventListener(type, stop, true);
      }, 100);
    },
    { capture: true, once: true },
  );
}

/**
 * Headless: while mounted, the next key, mouse button or wheel direction is reported to
 * `onCapture` and swallowed (the page never sees it, Mouse2 does not open the context menu).
 * Escape calls `onCancel` and never reaches the app's "back" handling.
 */
export function KeybindCapture({ onCapture, onCancel }: KeybindCaptureProps) {
  const latest = useRef({ onCapture, onCancel });
  latest.current = { onCapture, onCancel };

  useEffect(() => {
    // Several events can arrive before the parent unmounts this component.
    let done = false;
    const finish = (binding: Binding | null) => {
      if (done) return;
      done = true;
      if (binding === null) latest.current.onCancel();
      else latest.current.onCapture(binding);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      stop(e);
      if (e.repeat) return; // the key press that opened the capture may still be repeating
      if (e.code === CANCEL_CODE) {
        finish(null);
        return;
      }
      const binding = bindingFromKeyboardEvent(e);
      if (binding !== null) finish(binding);
    };
    const onMouseDown = (e: MouseEvent) => {
      stop(e);
      const binding = bindingFromMouseButton(e.button);
      if (binding === null) return;
      swallowRestOfPress();
      finish(binding);
    };
    const onWheel = (e: WheelEvent) => {
      stop(e);
      const binding = bindingFromWheel(e.deltaY);
      if (binding !== null) finish(binding);
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
    window.addEventListener("contextmenu", stop, true);
    const popEscape = pushEscapeHandler(() => finish(null));
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("wheel", onWheel, { capture: true });
      window.removeEventListener("contextmenu", stop, true);
      popEscape();
    };
  }, []);

  return null;
}
